import React, { useCallback } from 'react';
import { View, Text, TextInput } from 'react-native';
import i18n from 'i18n-js';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import ToggleSwitch from '@v2/elements/ToggleSwitch';
import { StepTimeProps, TimeBadgeProps, TimeData } from '@components/common/NCFileType/NCFileWizardNoERP/types';
import { TIME_FORMAT_HINT, TIME_PLACEHOLDER } from '@components/common/NCFileType/NCFileWizardNoERP/constants';
import { parseTimeToSeconds, formatSecondsToTime } from '@helpers';

const TimeBadge: React.FC<TimeBadgeProps> = ({ label, color, C, scale, font }) => (
  <View
    style={{
      backgroundColor: color,
      borderRadius: scale(20),
      paddingHorizontal: scale(8),
      paddingVertical: scale(2),
      marginRight: scale(4),
    }}
  >
    <Text style={[font['Caption 1'], { color: C('Label/Light/AlwaysLight Primary') }]}>{i18n.t(label)}</Text>
  </View>
);

const TimeDisplayOverlay: React.FC<{
  value: string;
  C: StepTimeProps['C'];
  scale: StepTimeProps['scale'];
  font: StepTimeProps['font'];
}> = ({ value, C, scale, font }) => {
  const seconds = parseTimeToSeconds(value);
  if (seconds <= 0) return null;

  const formatted = formatSecondsToTime(seconds);
  const lastColonIdx = formatted.lastIndexOf(':');
  const hmmPart = formatted.substring(0, lastColonIdx);
  const ssPart = formatted.substring(lastColonIdx);

  return (
    <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: scale(4) }}>
      <Text style={[font.Body, { color: C('Label/Light/Primary') }]}>{hmmPart}</Text>
      <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>{ssPart}</Text>
    </View>
  );
};

const StepTime: React.FC<StepTimeProps> = ({ timeData, setTimeData, C, scale, font }) => {
  const { paidCycleTime, paidSetupTime, activeTime, changeTime, isManualSplit } = timeData;

  const updateField = useCallback(
    (field: keyof TimeData, value: string | boolean) => {
      setTimeData({ ...timeData, [field]: value });
    },
    [timeData, setTimeData],
  );

  const normalizeTimeField = useCallback(
    (field: keyof TimeData) => {
      const raw = timeData[field];
      if (typeof raw !== 'string' || !raw.trim()) return;
      const seconds = parseTimeToSeconds(raw);
      if (seconds > 0) {
        setTimeData({ ...timeData, [field]: formatSecondsToTime(seconds) });
      }
    },
    [timeData, setTimeData],
  );

  const handleActiveTimeBlur = useCallback(() => {
    const rawActive = timeData.activeTime;
    if (typeof rawActive !== 'string' || !rawActive.trim()) return;
    const activeSec = parseTimeToSeconds(rawActive);
    const changeSec = parseTimeToSeconds(timeData.changeTime);
    const updates: Partial<TimeData> = {};
    if (activeSec > 0) {
      updates.activeTime = formatSecondsToTime(activeSec);
    }
    updates.paidCycleTime = formatSecondsToTime(activeSec + changeSec);
    setTimeData({ ...timeData, ...updates });
  }, [timeData, setTimeData]);

  const handleChangeTimeBlur = useCallback(() => {
    const rawChange = timeData.changeTime;
    if (typeof rawChange !== 'string' || !rawChange.trim()) return;
    const activeSec = parseTimeToSeconds(timeData.activeTime);
    const changeSec = parseTimeToSeconds(rawChange);
    const updates: Partial<TimeData> = {};
    if (changeSec > 0) {
      updates.changeTime = formatSecondsToTime(changeSec);
    }
    updates.paidCycleTime = formatSecondsToTime(activeSec + changeSec);
    setTimeData({ ...timeData, ...updates });
  }, [timeData, setTimeData]);

  const handleCycleTimeChange = useCallback(
    (value: string) => {
      const updates: Partial<TimeData> = { paidCycleTime: value };
      if (!isManualSplit) {
        const total = parseTimeToSeconds(value);
        if (total > 0) {
          const act = Math.round(total * 0.7);
          updates.activeTime = formatSecondsToTime(act);
          updates.changeTime = formatSecondsToTime(total - act);
        } else {
          updates.activeTime = '';
          updates.changeTime = '';
        }
      }
      setTimeData({ ...timeData, ...updates });
    },
    [timeData, setTimeData, isManualSplit],
  );

  const handleManualSplitToggle = useCallback(
    (val: boolean) => {
      const updates: Partial<TimeData> = { isManualSplit: val };
      if (val) {
        const total = parseTimeToSeconds(paidCycleTime);
        if (total > 0) {
          const act = Math.round(total * 0.7);
          updates.activeTime = formatSecondsToTime(act);
          updates.changeTime = formatSecondsToTime(total - act);
        }
      }
      setTimeData({ ...timeData, ...updates });
    },
    [timeData, setTimeData, paidCycleTime],
  );

  const inputStyle = [
    font.Body,
    {
      flex: 1,
      color: C('Label/Light/Primary'),
      paddingVertical: scale(8),
      paddingHorizontal: scale(12),
      backgroundColor: C('Background/Light/Panel'),
      borderRadius: scale(6),
      outlineStyle: 'none' as const, 
    },
  ];

  return (
    <View style={{ paddingHorizontal: scale(15) }}>
      {/* Paid Cycle Time Card */}
      <View
        style={{
          backgroundColor: C('Background/Light/Field lighter'),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          padding: scale(16),
          marginBottom: scale(12),
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: scale(12) }}>
          <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), marginRight: scale(8) }]}>
            {i18n.t('Paid Cycle Time')}
          </Text>
          <TimeBadge label="Active" color={C('Status/Light/Active')} C={C} scale={scale} font={font} />
          <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginHorizontal: scale(2) }]}>+ </Text>
          <TimeBadge label="Change" color={C('Status/Light/Change')} C={C} scale={scale} font={font} />
        </View>

        <TextInput
          style={inputStyle}
          placeholder={TIME_PLACEHOLDER}
          placeholderTextColor={C('Label/Light/Tertiary')}
          value={paidCycleTime}
          onChangeText={handleCycleTimeChange}
          onBlur={() => normalizeTimeField('paidCycleTime')}
          autoCorrect={false}
        />
        <TimeDisplayOverlay value={paidCycleTime} C={C} scale={scale} font={font} />

        {!isManualSplit && paidCycleTime.length > 0 && (
          <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginTop: scale(6) }]}>
            {i18n.t('Auto-split into Active & Change based on ideal ratio')}
          </Text>
        )}

        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: scale(12) }}>
          <ToggleSwitch
            isOn={isManualSplit}
            onToggle={handleManualSplitToggle}
            size="medium"
            onColor={C('System/Light/Blue')}
          />
          <Text style={[font.Footnote, { color: C('Label/Light/Primary'), marginLeft: scale(8) }]}>
            {i18n.t('Override: manually split Active & Change')}
          </Text>
        </View>

        {isManualSplit && (
          <View style={{ marginTop: scale(12) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: scale(8) }}>
              <View
                style={{
                  width: scale(8),
                  height: scale(8),
                  borderRadius: scale(4),
                  backgroundColor: C('Status/Light/Active'),
                  marginRight: scale(8),
                }}
              />
              <Text style={[font.Footnote, { color: C('Label/Light/Secondary'), width: scale(80) }]}>{i18n.t('Active Time')}</Text>
              <TextInput
                style={inputStyle}
                placeholder={TIME_PLACEHOLDER}
                placeholderTextColor={C('Label/Light/Tertiary')}
                value={activeTime}
                onChangeText={(v) => updateField('activeTime', v)}
                onBlur={handleActiveTimeBlur}
                autoCorrect={false}
              />
            </View>
            <TimeDisplayOverlay value={activeTime} C={C} scale={scale} font={font} />
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: scale(8) }}>
              <View
                style={{
                  width: scale(8),
                  height: scale(8),
                  borderRadius: scale(4),
                  backgroundColor: C('Status/Light/Change'),
                  marginRight: scale(8),
                }}
              />
              <Text style={[font.Footnote, { color: C('Label/Light/Secondary'), width: scale(80) }]}>{i18n.t('Change Time')}</Text>
              <TextInput
                style={inputStyle}
                placeholder={TIME_PLACEHOLDER}
                placeholderTextColor={C('Label/Light/Tertiary')}
                value={changeTime}
                onChangeText={(v) => updateField('changeTime', v)}
                onBlur={handleChangeTimeBlur}
                autoCorrect={false}
              />
            </View>
            <TimeDisplayOverlay value={changeTime} C={C} scale={scale} font={font} />
          </View>
        )}
      </View>

      {/* Paid Setup Time Card */}
      <View
        style={{
          backgroundColor: C('Background/Light/Field lighter'),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          padding: scale(16),
          marginBottom: scale(12),
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: scale(12) }}>
          <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), marginRight: scale(8) }]}>
            {i18n.t('Paid Setup Time')}
          </Text>
          <TimeBadge label="Setup" color={C('Status/Light/Setup')} C={C} scale={scale} font={font} />
        </View>

        <TextInput
          style={inputStyle}
          placeholder={TIME_PLACEHOLDER}
          placeholderTextColor={C('Label/Light/Tertiary')}
          value={paidSetupTime}
          onChangeText={(v) => updateField('paidSetupTime', v)}
          onBlur={() => normalizeTimeField('paidSetupTime')}
          autoCorrect={false}
        />
        <TimeDisplayOverlay value={paidSetupTime} C={C} scale={scale} font={font} />
      </View>

      <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(TIME_FORMAT_HINT)}</Text>
    </View>
  );
};

export default StepTime;
